<template>
  <div class="learning-paths-page">
    <div class="page-header">
      <div class="header-content">
        <h1 class="page-title">
          <i class="fas fa-route"></i>
          Learning Paths
        </h1>
        <p class="page-subtitle">Structured learning journeys to master your skills</p>
      </div>
      
      <div class="header-actions">
        <router-link to="/learning" class="btn btn-secondary">
          <i class="fas fa-book-open"></i>
          Browse Courses
        </router-link>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p>Loading learning paths...</p>
    </div>

    <!-- Main Content -->
    <div v-else>
      <!-- Learning Paths Grid -->
      <div v-if="learningPaths.length > 0" class="paths-grid">
        <div
          v-for="path in learningPaths"
          :key="path.id"
          class="path-card"
        >
          <div class="path-header">
            <div class="path-icon">
              <i class="fas fa-route"></i>
            </div>
            <div class="path-badge" :class="`difficulty-${path.difficulty_level?.toLowerCase()}`">
              {{ path.difficulty_level }}
            </div>
          </div>

          <div class="path-content">
            <h2 class="path-title">{{ path.title }}</h2>
            <p class="path-description">{{ path.description }}</p>

            <div class="path-stats">
              <div class="stat">
                <i class="fas fa-book"></i>
                <span>{{ path.course_count || 0 }} Courses</span>
              </div>
              <div class="stat" v-if="path.estimated_duration_hours">
                <i class="fas fa-clock"></i>
                <span>{{ path.estimated_duration_hours }} hours</span>
              </div>
              <div class="stat" v-if="path.completion_certificate">
                <i class="fas fa-certificate"></i>
                <span>Certificate</span>
              </div>
            </div>

            <div v-if="path.prerequisites" class="prerequisites">
              <h4><i class="fas fa-info-circle"></i> Prerequisites</h4>
              <p>{{ path.prerequisites }}</p>
            </div>

            <div class="path-actions">
              <button @click="viewPathDetails(path)" class="btn btn-primary btn-block">
                <i class="fas fa-arrow-right"></i>
                View Path Details
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State for Learning Paths -->
      <div v-else class="empty-state learning-paths-empty">
        <i class="fas fa-route"></i>
        <h2>No Learning Paths Available</h2>
        <p>Learning paths are coming soon. Check back later or browse our course catalog.</p>
        <router-link to="/learning" class="btn btn-primary">
          <i class="fas fa-search"></i>
          Browse Courses
        </router-link>
      </div>

      <!-- Research Library Section -->
      <div class="research-library-section">
        <div class="section-header-row">
          <div class="section-title">
            <h2>
              <i class="fas fa-book-medical"></i>
              My Research Library
            </h2>
            <p class="section-subtitle">All your saved research articles organized by course</p>
          </div>
          <div class="library-stats" v-if="researchStats">
            <div class="stat-pill">
              <i class="fas fa-file-alt"></i>
              {{ researchStats.total_saved || 0 }} Articles
            </div>
            <div class="stat-pill" v-if="researchStats.favorites > 0">
              <i class="fas fa-star"></i>
              {{ researchStats.favorites }} Favorites
            </div>
          </div>
        </div>

        <div v-if="loadingResearch" class="loading-container">
          <div class="spinner"></div>
          <p>Loading research library...</p>
        </div>

        <div v-else-if="coursesWithResources.length > 0" class="research-by-course">
          <div
            v-for="courseGroup in coursesWithResources"
            :key="courseGroup.course.id"
            class="course-research-card"
          >
            <div class="course-research-header">
              <div class="course-info">
                <router-link :to="`/learning/courses/${courseGroup.course.id}`" class="course-link">
                  <h3>{{ courseGroup.course.title }}</h3>
                </router-link>
                <span class="resource-count">{{ courseGroup.resource_count }} articles saved</span>
              </div>
              <router-link 
                :to="`/learning/courses/${courseGroup.course.id}?tab=research`"
                class="btn btn-sm btn-secondary"
              >
                View All
                <i class="fas fa-arrow-right"></i>
              </router-link>
            </div>

            <div class="course-resources-preview">
              <div
                v-for="resource in courseGroup.resources.slice(0, 3)"
                :key="resource.id"
                class="resource-preview-item"
              >
                <div class="resource-preview-content">
                  <a :href="resource.url" target="_blank" rel="noopener" class="resource-link">
                    {{ truncateText(resource.title, 80) }}
                  </a>
                  <div class="resource-preview-meta">
                    <span v-if="resource.journal">{{ resource.journal }}</span>
                    <span v-if="resource.publication_date">{{ resource.publication_date }}</span>
                  </div>
                </div>
                <i v-if="resource.is_favorite" class="fas fa-star favorite-icon"></i>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="empty-state research-empty">
          <i class="fas fa-book-medical"></i>
          <h3>No Research Saved Yet</h3>
          <p>As you watch course videos, search for and save relevant research articles to build your personal library.</p>
          <router-link to="/my-learning" class="btn btn-primary">
            <i class="fas fa-play"></i>
            Continue Learning
          </router-link>
        </div>
      </div>
    </div>

    <!-- Path Details Modal -->
    <div v-if="showModal && selectedPath" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <button @click="closeModal" class="modal-close">
          <i class="fas fa-times"></i>
        </button>

        <div class="modal-header">
          <h2>{{ selectedPath.title }}</h2>
          <div class="path-badge" :class="`difficulty-${selectedPath.difficulty_level?.toLowerCase()}`">
            {{ selectedPath.difficulty_level }}
          </div>
        </div>

        <div class="modal-body">
          <p class="path-description">{{ selectedPath.description }}</p>

          <div class="path-stats">
            <div class="stat">
              <i class="fas fa-book"></i>
              <span>{{ selectedPath.course_count || 0 }} Courses</span>
            </div>
            <div class="stat" v-if="selectedPath.estimated_duration_hours">
              <i class="fas fa-clock"></i>
              <span>{{ selectedPath.estimated_duration_hours }} hours</span>
            </div>
            <div class="stat" v-if="selectedPath.completion_certificate">
              <i class="fas fa-certificate"></i>
              <span>Certificate Upon Completion</span>
            </div>
          </div>

          <div v-if="selectedPath.prerequisites" class="section">
            <h3><i class="fas fa-info-circle"></i> Prerequisites</h3>
            <p>{{ selectedPath.prerequisites }}</p>
          </div>

          <div v-if="pathCourses.length > 0" class="section">
            <h3><i class="fas fa-list"></i> Courses in This Path</h3>
            <div class="courses-list">
              <div
                v-for="(course, index) in pathCourses"
                :key="course.id"
                class="course-item"
              >
                <div class="course-index">{{ index + 1 }}</div>
                <div class="course-info">
                  <h4>{{ course.title }}</h4>
                  <p>{{ course.short_description }}</p>
                  <div class="course-meta">
                    <span v-if="course.estimated_duration_hours">
                      <i class="fas fa-clock"></i>
                      {{ course.estimated_duration_hours }}h
                    </span>
                  </div>
                </div>
                <router-link
                  :to="`/learning/courses/${course.id}`"
                  class="btn btn-small btn-secondary"
                >
                  View
                </router-link>
              </div>
            </div>
          </div>

          <div class="modal-actions">
            <button @click="startLearningPath" class="btn btn-primary btn-large">
              <i class="fas fa-play"></i>
              Start Learning Path
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { lmsAPI } from '@/services/lmsAPI.js'
import { useToast } from 'vue-toastification'
import whitelabelBranding from '@/services/whitelabelBrandingService'

export default {
  name: 'LearningPaths',
  setup() {
    const router = useRouter()
    
    // Get organization colors for theming
    const primaryColor = computed(() => whitelabelBranding.getColor('primary') || '#667eea')
    const secondaryColor = computed(() => whitelabelBranding.getColor('secondary') || '#764ba2')
    const accentColor = computed(() => whitelabelBranding.getColor('accent') || '#667eea')
    const toast = useToast()

    const learningPaths = ref([])
    const loading = ref(true)
    const showModal = ref(false)
    const selectedPath = ref(null)
    const pathCourses = ref([])
    
    // Research Library state
    const coursesWithResources = ref([])
    const researchStats = ref(null)
    const loadingResearch = ref(false)

    const loadLearningPaths = async () => {
      loading.value = true
      try {
        const response = await lmsAPI.getLearningPaths()
        learningPaths.value = response.data.results || response.data
      } catch (err) {
        console.error('Error loading learning paths:', err)
        toast.error('Failed to load learning paths')
      } finally {
        loading.value = false
      }
    }

    const viewPathDetails = async (path) => {
      selectedPath.value = path
      showModal.value = true

      // Load courses in this path
      try {
        const response = await lmsAPI.getLearningPath(path.id)
        pathCourses.value = response.data.courses || []
      } catch (err) {
        console.error('Error loading path courses:', err)
        toast.error('Failed to load path details')
      }
    }

    const closeModal = () => {
      showModal.value = false
      selectedPath.value = null
      pathCourses.value = []
    }

    const startLearningPath = () => {
      if (pathCourses.value.length > 0) {
        // Navigate to first course in the path
        router.push(`/learning/courses/${pathCourses.value[0].id}`)
      } else {
        toast.info('This learning path has no courses yet')
      }
    }
    
    const loadResearchLibrary = async () => {
      loadingResearch.value = true
      try {
        // Load resources grouped by course
        const resourcesResponse = await lmsAPI.getResourcesByCourse()
        coursesWithResources.value = resourcesResponse.data.courses_with_resources || []
        
        // Load stats
        const statsResponse = await lmsAPI.getResourceStats()
        researchStats.value = statsResponse.data.stats || null
      } catch (err) {
        console.error('Error loading research library:', err)
        // Non-critical - don't show error toast
      } finally {
        loadingResearch.value = false
      }
    }
    
    const truncateText = (text, maxLength) => {
      if (!text || text.length <= maxLength) return text
      return text.substring(0, maxLength) + '...'
    }

    onMounted(() => {
      loadLearningPaths()
      loadResearchLibrary()
    })

    return {
      learningPaths,
      loading,
      showModal,
      selectedPath,
      pathCourses,
      viewPathDetails,
      closeModal,
      startLearningPath,
      primaryColor,
      secondaryColor,
      accentColor,
      // Research Library
      coursesWithResources,
      researchStats,
      loadingResearch,
      truncateText
    }
  }
}
</script>

<style scoped>
.learning-paths-page {
  max-width: 1600px;
  margin: 0 auto;
  padding: var(--spacing-lg);
}

/* Page Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--spacing-xl);
  padding-bottom: var(--spacing-lg);
  border-bottom: 2px solid var(--color-border);
}

.header-content {
  flex: 1;
}

.page-title {
  font-size: 2rem;
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0 0 var(--spacing-sm) 0;
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

.page-title i {
  color: var(--color-primary);
}

.page-subtitle {
  font-size: 1.1rem;
  color: var(--color-text-secondary);
  margin: 0;
}

.header-actions {
  display: flex;
  gap: var(--spacing-md);
}

/* Loading State */
.loading-container {
  text-align: center;
  padding: var(--spacing-xxl);
}

.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: var(--color-primary);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto var(--spacing-md);
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Paths Grid */
.paths-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: var(--spacing-xl);
}

.path-card {
  background: white;
  border-radius: var(--border-radius);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  overflow: hidden;
}

.path-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

.path-header {
  background: linear-gradient(135deg, v-bind(primaryColor) 0%, v-bind(secondaryColor) 100%);
  padding: var(--spacing-xl);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.path-icon {
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  color: white;
}

.path-badge {
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.difficulty-beginner {
  background: #e8f5e9;
  color: #388e3c;
}

.difficulty-intermediate {
  background: #fff3e0;
  color: #f57c00;
}

.difficulty-advanced {
  background: #fce4ec;
  color: #c2185b;
}

.path-content {
  padding: var(--spacing-xl);
}

.path-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0 0 var(--spacing-md) 0;
}

.path-description {
  color: var(--color-text-secondary);
  line-height: 1.6;
  margin: 0 0 var(--spacing-lg) 0;
}

.path-stats {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-lg);
  padding: var(--spacing-md);
  background: #f8f9fa;
  border-radius: 6px;
}

.stat {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  color: var(--color-text-secondary);
  font-size: 0.9rem;
}

.stat i {
  color: var(--color-primary);
}

.prerequisites {
  padding: var(--spacing-md);
  background: #fff3cd;
  border-left: 4px solid #ffc107;
  border-radius: 4px;
  margin-bottom: var(--spacing-lg);
}

.prerequisites h4 {
  margin: 0 0 var(--spacing-xs) 0;
  color: #856404;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.prerequisites p {
  margin: 0;
  color: #856404;
  font-size: 0.9rem;
}

.path-actions {
  display: flex;
  gap: var(--spacing-sm);
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-lg);
  border-radius: var(--border-radius);
  border: none;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
}

.btn-block {
  width: 100%;
}

.btn-small {
  padding: var(--spacing-xs) var(--spacing-sm);
  font-size: 0.9rem;
}

.btn-large {
  padding: var(--spacing-md) var(--spacing-xl);
  font-size: 1.1rem;
}

.btn-primary {
  background: var(--color-primary);
  color: white;
}

.btn-primary:hover {
  background: var(--color-secondary);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.btn-secondary {
  background: white;
  color: var(--color-primary);
  border: 2px solid var(--color-primary);
}

.btn-secondary:hover {
  background: var(--color-primary);
  color: white;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: var(--spacing-xxl);
  background: white;
  border-radius: var(--border-radius);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.empty-state i {
  font-size: 4rem;
  color: var(--color-primary);
  opacity: 0.5;
  margin-bottom: var(--spacing-lg);
}

.empty-state h2 {
  font-size: 1.5rem;
  margin: 0 0 var(--spacing-md) 0;
  color: var(--color-text-primary);
}

.empty-state p {
  font-size: 1.1rem;
  color: var(--color-text-secondary);
  margin: 0 0 var(--spacing-xl) 0;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: var(--spacing-lg);
}

.modal-content {
  background: white;
  border-radius: var(--border-radius);
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
}

.modal-close {
  position: absolute;
  top: var(--spacing-md);
  right: var(--spacing-md);
  background: none;
  border: none;
  font-size: 1.5rem;
  color: var(--color-text-secondary);
  cursor: pointer;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.modal-close:hover {
  background: #f8f9fa;
  color: var(--color-text-primary);
}

.modal-header {
  padding: var(--spacing-xl);
  border-bottom: 2px solid var(--color-border);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--spacing-md);
}

.modal-header h2 {
  margin: 0;
  font-size: 1.8rem;
  color: var(--color-text-primary);
  flex: 1;
}

.modal-body {
  padding: var(--spacing-xl);
}

.section {
  margin-bottom: var(--spacing-xl);
}

.section h3 {
  font-size: 1.2rem;
  margin: 0 0 var(--spacing-md) 0;
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.courses-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.course-item {
  display: flex;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  background: #f8f9fa;
  border-radius: 6px;
  align-items: center;
}

.course-index {
  width: 40px;
  height: 40px;
  background: var(--color-primary);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  flex-shrink: 0;
}

.course-info {
  flex: 1;
  min-width: 0;
}

.course-info h4 {
  margin: 0 0 var(--spacing-xs) 0;
  font-size: 1rem;
  color: var(--color-text-primary);
}

.course-info p {
  margin: 0 0 var(--spacing-xs) 0;
  color: var(--color-text-secondary);
  font-size: 0.9rem;
}

.course-meta {
  display: flex;
  gap: var(--spacing-sm);
  font-size: 0.85rem;
  color: var(--color-text-secondary);
}

.course-meta i {
  color: var(--color-primary);
}

.modal-actions {
  display: flex;
  justify-content: center;
  padding-top: var(--spacing-lg);
  border-top: 1px solid var(--color-border);
}

/* Responsive */
@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    gap: var(--spacing-md);
  }

  .page-title {
    font-size: 1.5rem;
  }

  .paths-grid {
    grid-template-columns: 1fr;
  }

  .modal-content {
    max-height: 95vh;
  }

  .course-item {
    flex-direction: column;
    align-items: flex-start;
  }
}

/* Research Library Section */
.research-library-section {
  margin-top: var(--spacing-xxl);
  padding-top: var(--spacing-xxl);
  border-top: 2px solid var(--color-border);
}

.section-header-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--spacing-xl);
  flex-wrap: wrap;
  gap: var(--spacing-md);
}

.section-title h2 {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin: 0 0 var(--spacing-xs) 0;
  font-size: 1.75rem;
  color: var(--color-text-primary);
}

.section-title h2 i {
  color: #10b981;
}

.section-subtitle {
  margin: 0;
  color: var(--color-text-secondary);
  font-size: 1rem;
}

.library-stats {
  display: flex;
  gap: var(--spacing-sm);
}

.stat-pill {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-xs) var(--spacing-md);
  background: #f0fdf4;
  color: #166534;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
}

.stat-pill i {
  color: #10b981;
}

.research-by-course {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.course-research-card {
  background: white;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  overflow: hidden;
  transition: all 0.2s ease;
}

.course-research-card:hover {
  border-color: #10b981;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.1);
}

.course-research-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-lg);
  background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
  border-bottom: 1px solid var(--color-border);
}

.course-research-header .course-info {
  flex: 1;
}

.course-link {
  text-decoration: none;
}

.course-link h3 {
  margin: 0 0 var(--spacing-xs) 0;
  color: var(--color-text-primary);
  transition: color 0.2s ease;
}

.course-link:hover h3 {
  color: #10b981;
}

.resource-count {
  font-size: 0.9rem;
  color: var(--color-text-secondary);
}

.course-resources-preview {
  padding: var(--spacing-md) var(--spacing-lg);
}

.resource-preview-item {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) 0;
  border-bottom: 1px solid var(--color-border);
}

.resource-preview-item:last-child {
  border-bottom: none;
}

.resource-preview-content {
  flex: 1;
  min-width: 0;
}

.resource-link {
  display: block;
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--color-text-primary);
  text-decoration: none;
  line-height: 1.4;
  margin-bottom: 4px;
}

.resource-link:hover {
  color: #10b981;
}

.resource-preview-meta {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-sm);
  font-size: 0.8rem;
  color: var(--color-text-secondary);
}

.favorite-icon {
  color: #f59e0b;
  font-size: 0.9rem;
  flex-shrink: 0;
}

.research-empty {
  background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
  border-radius: var(--radius-lg);
  padding: var(--spacing-xxl);
  text-align: center;
}

.research-empty i {
  font-size: 3rem;
  color: #10b981;
  opacity: 0.6;
  margin-bottom: var(--spacing-md);
}

.research-empty h3 {
  margin: 0 0 var(--spacing-sm) 0;
  color: var(--color-text-primary);
}

.research-empty p {
  margin: 0 auto var(--spacing-lg);
  max-width: 400px;
  color: var(--color-text-secondary);
}

@media (max-width: 768px) {
  .section-header-row {
    flex-direction: column;
    align-items: stretch;
  }
  
  .library-stats {
    justify-content: flex-start;
  }
  
  .course-research-header {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--spacing-md);
  }
}
</style>

